import {
  ForecastMapBaseOverlays,
  ForecastMapCapabilities,
  ForecastMapCapabilityModel,
  ForecastMapConfig,
  ForecastMapContourLevels,
  ForecastMapContourLevelsMode,
  ForecastMapLayer,
  ForecastMapLayerType,
  ForecastMapNanPolicy,
  ForecastMapParameters,
  ForecastMapProjection,
  ForecastMapUnitConversion,
  ForecastMapVectorLayer,
} from '../../api/types';

/*
Pure draft model for the map composer.

The draft mirrors ForecastMapParameters but keeps free-text numeric fields
as strings (so partially typed values never crash the form) and keeps the
layer stack in VISUAL order: index 0 is the TOP of the stack. The backend
renders by matplotlib z-order (higher z_order paints on top; presets store
layers bottom-first with ascending z_order), so serialization reverses the
draft list and assigns z_order = position + 1 from the bottom.
*/

// --- Types ------------------------------------------------------------------

export type ColormapDraft =
  | {mode: 'preset'; preset: string}
  | {mode: 'custom'; colors: string[]};

export type ContourLevelsDraft = {
  enabled: boolean;
  mode: ForecastMapContourLevelsMode;
  count: string;
  step: string;
  values: string; // comma-separated ascending numbers
};

export type LayerDraft = {
  key: string; // stable identity for React lists / reducer targeting
  layer_type: ForecastMapLayerType;
  display_name: string;
  colormap: ColormapDraft;
  metric: string; // scalar layers
  u_metric: string; // vector / magnitude layers
  v_metric: string;
  min_value: string;
  max_value: string;
  opacity: number;
  nan_policy: ForecastMapNanPolicy;
  unit_conversion: ForecastMapUnitConversion;
  contour_levels: ContourLevelsDraft;
  downscaling: string; // vector layers
};

export type ExtentDraft = {
  min_lon: string;
  max_lon: string;
  min_lat: string;
  max_lat: string;
};

export type BuilderDraft = {
  name: string;
  description: string;
  modelId: number | null;
  layers: LayerDraft[]; // index 0 = top of the visual stack
  extentEnabled: boolean;
  extent: ExtentDraft;
  projection: ForecastMapProjection;
  height: string;
  dpi: string;
  // Opaque passthrough (no composer UI yet): base overlays loaded from an
  // edited/forked config must survive the save round-trip instead of
  // silently resetting to backend defaults.
  baseOverlays?: ForecastMapBaseOverlays;
};

// Backend schema: display_name has max_length=128 (layers.py).
export const DISPLAY_NAME_MAX_LENGTH = 128;

// --- Layer-type helpers -----------------------------------------------------

const SCALAR_TYPES: ForecastMapLayerType[] = [
  'filled_contour',
  'contour',
  'raster',
];
const VECTOR_TYPES: ForecastMapLayerType[] = ['barbs', 'vector_field'];

export const isScalarType = (layerType: ForecastMapLayerType) =>
  SCALAR_TYPES.includes(layerType);

export const isVectorType = (layerType: ForecastMapLayerType) =>
  VECTOR_TYPES.includes(layerType);

export const usesUvPair = (layerType: ForecastMapLayerType) =>
  isVectorType(layerType) || layerType === 'magnitude';

// Contour levels apply to contour-drawing layers (raster uses plain
// vmin/vmax pcolormesh; vector layers draw glyphs).
export const supportsContourLevels = (layerType: ForecastMapLayerType) =>
  layerType === 'filled_contour' ||
  layerType === 'contour' ||
  layerType === 'magnitude';

// --- Constructors -----------------------------------------------------------

const EMPTY_LEVELS: ContourLevelsDraft = {
  enabled: false,
  mode: 'linspace',
  count: '10',
  step: '',
  values: '',
};

let keyCounter = 0;
export const nextLayerKey = () => {
  keyCounter += 1;
  return `layer-${keyCounter}`;
};

export const createLayerDraft = (
  layerType: ForecastMapLayerType,
  displayName: string,
): LayerDraft => ({
  key: nextLayerKey(),
  layer_type: layerType,
  display_name: displayName,
  colormap: {mode: 'preset', preset: 'viridis'},
  metric: '',
  u_metric: '',
  v_metric: '',
  min_value: '',
  max_value: '',
  opacity: 1,
  nan_policy: 'skip',
  unit_conversion: 'none',
  contour_levels: {...EMPTY_LEVELS},
  downscaling: '1',
});

export const createEmptyDraft = (modelId: number | null): BuilderDraft => ({
  name: '',
  description: '',
  modelId,
  layers: [],
  extentEnabled: false,
  extent: {min_lon: '', max_lon: '', min_lat: '', max_lat: ''},
  // Backend defaults (schema: plate_carree / 16.0 in / 200 dpi).
  projection: 'plate_carree',
  height: '16',
  dpi: '200',
});

// --- Config → draft ---------------------------------------------------------

const numberToField = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

const colormapToDraft = (
  colormap: ForecastMapLayer['colormap'],
): ColormapDraft =>
  Array.isArray(colormap)
    ? {mode: 'custom', colors: [...colormap]}
    : {mode: 'preset', preset: colormap};

const levelsToDraft = (
  levels: ForecastMapContourLevels | null | undefined,
): ContourLevelsDraft => {
  if (!levels) {
    return {...EMPTY_LEVELS};
  }
  return {
    enabled: true,
    mode: levels.mode,
    count: numberToField(levels.count),
    step: numberToField(levels.step),
    values: (levels.values ?? []).join(', '),
  };
};

const layerToDraft = (layer: ForecastMapLayer): LayerDraft => {
  const base = {
    key: nextLayerKey(),
    layer_type: layer.layer_type,
    display_name: layer.display_name,
    colormap: colormapToDraft(layer.colormap),
    min_value: numberToField(layer.min_value),
    max_value: numberToField(layer.max_value),
    opacity: layer.opacity ?? 1,
    nan_policy: layer.nan_policy ?? ('skip' as ForecastMapNanPolicy),
    unit_conversion:
      'unit_conversion' in layer && layer.unit_conversion
        ? layer.unit_conversion
        : ('none' as ForecastMapUnitConversion),
  };
  // Positive checks only: TS cannot negatively narrow the discriminant on
  // these intersection types, so the last branch casts explicitly.
  if (
    layer.layer_type === 'filled_contour' ||
    layer.layer_type === 'contour' ||
    layer.layer_type === 'raster'
  ) {
    return {
      ...base,
      metric: layer.metric,
      u_metric: '',
      v_metric: '',
      contour_levels: levelsToDraft(layer.contour_levels),
      downscaling: '1',
    };
  }
  if (layer.layer_type === 'magnitude') {
    return {
      ...base,
      metric: '',
      u_metric: layer.u_metric,
      v_metric: layer.v_metric,
      contour_levels: levelsToDraft(layer.contour_levels),
      downscaling: '1',
    };
  }
  const vector = layer as ForecastMapVectorLayer;
  return {
    ...base,
    metric: '',
    u_metric: vector.u_metric,
    v_metric: vector.v_metric,
    contour_levels: {...EMPTY_LEVELS},
    downscaling: numberToField(vector.downscaling ?? 1),
  };
};

/**
 * Resolve a config's nested forecast_model summary to a capabilities model
 * pk. Newer payloads nest the id; older ones are matched by name+mesh.
 */
export const resolveModelId = (
  config: ForecastMapConfig,
  capabilities: ForecastMapCapabilities,
): number | null => {
  if (typeof config.forecast_model.id === 'number') {
    return config.forecast_model.id;
  }
  const match = capabilities.models.find(
    (model) =>
      model.name === config.forecast_model.name &&
      model.mesh_size === config.forecast_model.mesh_size,
  );
  return match?.id ?? null;
};

export const draftFromConfig = (
  config: ForecastMapConfig,
  capabilities: ForecastMapCapabilities,
): BuilderDraft => {
  const parameters = config.parameters;
  // Stored bottom-first with ascending z_order; the draft wants top-first.
  const sorted = [...parameters.layers].sort(
    (a, b) => (a.z_order ?? 1) - (b.z_order ?? 1),
  );
  return {
    name: config.name,
    description: config.description ?? '',
    modelId: resolveModelId(config, capabilities),
    layers: sorted.map(layerToDraft).reverse(),
    extentEnabled: Boolean(parameters.extent),
    extent: parameters.extent
      ? {
          min_lon: numberToField(parameters.extent.min_lon),
          max_lon: numberToField(parameters.extent.max_lon),
          min_lat: numberToField(parameters.extent.min_lat),
          max_lat: numberToField(parameters.extent.max_lat),
        }
      : {min_lon: '', max_lon: '', min_lat: '', max_lat: ''},
    projection: parameters.projection ?? 'plate_carree',
    height: numberToField(parameters.height ?? 16),
    dpi: numberToField(parameters.dpi ?? 200),
    // Opaque passthrough — preserved verbatim on save (edits and forks
    // must not reset non-default overlays).
    baseOverlays: parameters.base_overlays,
  };
};

// --- Reducer ----------------------------------------------------------------

export type BuilderDraftAction =
  | {type: 'setName'; name: string}
  | {type: 'setDescription'; description: string}
  | {type: 'setModel'; modelId: number}
  // The caller constructs the layer (createLayerDraft) so it knows the new
  // key, e.g. to auto-expand the editor.
  | {type: 'addLayer'; layer: LayerDraft}
  | {type: 'removeLayer'; key: string}
  // direction -1 moves toward the top of the stack (index 0).
  | {type: 'moveLayer'; key: string; direction: -1 | 1}
  | {type: 'updateLayer'; key: string; patch: Partial<Omit<LayerDraft, 'key'>>}
  | {
      type: 'updateLayerLevels';
      key: string;
      patch: Partial<ContourLevelsDraft>;
    }
  | {type: 'setProjection'; projection: ForecastMapProjection}
  | {type: 'setHeight'; height: string}
  | {type: 'setDpi'; dpi: string}
  | {type: 'setExtentEnabled'; enabled: boolean}
  | {type: 'updateExtent'; patch: Partial<ExtentDraft>};

export const draftReducer = (
  draft: BuilderDraft,
  action: BuilderDraftAction,
): BuilderDraft => {
  switch (action.type) {
    case 'setName':
      return {...draft, name: action.name};
    case 'setDescription':
      return {...draft, description: action.description};
    case 'setModel':
      // Layers are kept as-is: validation flags metrics the new model
      // lacks instead of silently discarding user work.
      return {...draft, modelId: action.modelId};
    case 'addLayer':
      // New layers go on top of the stack (Photoshop-style).
      return {...draft, layers: [action.layer, ...draft.layers]};
    case 'removeLayer':
      return {
        ...draft,
        layers: draft.layers.filter((layer) => layer.key !== action.key),
      };
    case 'moveLayer': {
      const index = draft.layers.findIndex((layer) => layer.key === action.key);
      const target = index + action.direction;
      if (index < 0 || target < 0 || target >= draft.layers.length) {
        return draft;
      }
      const layers = [...draft.layers];
      const [moved] = layers.splice(index, 1);
      layers.splice(target, 0, moved);
      return {...draft, layers};
    }
    case 'updateLayer':
      return {
        ...draft,
        layers: draft.layers.map((layer) =>
          layer.key === action.key ? {...layer, ...action.patch} : layer,
        ),
      };
    case 'updateLayerLevels':
      return {
        ...draft,
        layers: draft.layers.map((layer) =>
          layer.key === action.key
            ? {
                ...layer,
                contour_levels: {...layer.contour_levels, ...action.patch},
              }
            : layer,
        ),
      };
    case 'setProjection':
      return {...draft, projection: action.projection};
    case 'setHeight':
      return {...draft, height: action.height};
    case 'setDpi':
      return {...draft, dpi: action.dpi};
    case 'setExtentEnabled':
      return {...draft, extentEnabled: action.enabled};
    case 'updateExtent':
      return {...draft, extent: {...draft.extent, ...action.patch}};
    default:
      return draft;
  }
};

// --- Validation -------------------------------------------------------------

// Mirrors the backend's color validator: #RGB / #RRGGBB / #RRGGBBAA or a
// small named allow-list.
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const NAMED_COLORS = new Set([
  'black',
  'white',
  'gray',
  'grey',
  'red',
  'green',
  'blue',
  'yellow',
  'cyan',
  'magenta',
  'transparent',
]);

export const isValidColor = (value: string) =>
  NAMED_COLORS.has(value.toLowerCase()) || HEX_COLOR_RE.test(value);

const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseValuesList = (raw: string): number[] | null => {
  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  if (parts.length < 2) {
    return null;
  }
  const values = parts.map((part) => Number(part));
  if (values.some((value) => !Number.isFinite(value))) {
    return null;
  }
  for (let i = 1; i < values.length; i += 1) {
    if (values[i] <= values[i - 1]) {
      return null; // must be strictly ascending
    }
  }
  return values;
};

const levelsAreValid = (levels: ContourLevelsDraft): boolean => {
  if (!levels.enabled) {
    return true;
  }
  if (levels.mode === 'linspace') {
    const count = parseNumber(levels.count);
    return count !== null && Number.isInteger(count) && count > 0;
  }
  if (levels.mode === 'arange') {
    const step = parseNumber(levels.step);
    return step !== null && step > 0;
  }
  return parseValuesList(levels.values) !== null;
};

// Backend rule: the legend must not depend on frame data. min/max are
// required for magnitude and raster always, and for filled_contour /
// contour unless explicit contour levels provide the boundaries.
const minMaxRequired = (layer: LayerDraft): boolean => {
  if (isVectorType(layer.layer_type)) {
    return false;
  }
  if (layer.layer_type === 'magnitude' || layer.layer_type === 'raster') {
    return true;
  }
  const hasExplicitLevels =
    layer.contour_levels.enabled &&
    layer.contour_levels.mode === 'explicit' &&
    parseValuesList(layer.contour_levels.values) !== null;
  return !hasExplicitLevels;
};

export type LayerValidation = {
  displayName?: 'required' | 'tooLong';
  metric?: 'required' | 'unavailable';
  uMetric?: 'required' | 'unavailable';
  vMetric?: 'required' | 'unavailable' | 'duplicate';
  minMax?: 'required' | 'order' | 'invalid';
  colormap?: 'tooFew' | 'invalidColor';
  levels?: 'invalid';
  downscaling?: 'invalid';
};

export type DraftValidation = {
  layerErrors: Record<string, LayerValidation>;
  model?: 'required';
  layersEmpty: boolean;
  tooManyLayers: boolean;
  extent?: 'invalid';
  height?: 'range';
  dpi?: 'range';
  budget: {used: number; limit: number; exceeded: boolean};
  nameMissing: boolean;
  // Everything a preview needs (name excluded).
  parametersValid: boolean;
  canSave: boolean;
};

const validateMetricField = (
  value: string,
  metrics: string[] | null,
): 'required' | 'unavailable' | undefined => {
  if (value === '') {
    return 'required';
  }
  if (metrics !== null && !metrics.includes(value)) {
    return 'unavailable';
  }
  return undefined;
};

const validateLayer = (
  layer: LayerDraft,
  metrics: string[] | null,
): LayerValidation => {
  const errors: LayerValidation = {};

  if (layer.display_name.trim() === '') {
    errors.displayName = 'required';
  } else if (layer.display_name.trim().length > DISPLAY_NAME_MAX_LENGTH) {
    errors.displayName = 'tooLong';
  }

  if (usesUvPair(layer.layer_type)) {
    errors.uMetric = validateMetricField(layer.u_metric, metrics);
    errors.vMetric = validateMetricField(layer.v_metric, metrics);
    if (
      !errors.vMetric &&
      layer.v_metric !== '' &&
      layer.v_metric === layer.u_metric
    ) {
      errors.vMetric = 'duplicate';
    }
  } else {
    errors.metric = validateMetricField(layer.metric, metrics);
  }

  const min = parseNumber(layer.min_value);
  const max = parseNumber(layer.max_value);
  const hasMinText = layer.min_value.trim() !== '';
  const hasMaxText = layer.max_value.trim() !== '';
  if (minMaxRequired(layer) && (!hasMinText || !hasMaxText)) {
    errors.minMax = 'required';
  } else if ((hasMinText && min === null) || (hasMaxText && max === null)) {
    errors.minMax = 'invalid';
  } else if (min !== null && max !== null && min >= max) {
    errors.minMax = 'order';
  }

  if (layer.colormap.mode === 'custom') {
    if (layer.colormap.colors.length < 2) {
      errors.colormap = 'tooFew';
    } else if (
      layer.colormap.colors.some((color) => !isValidColor(color.trim()))
    ) {
      errors.colormap = 'invalidColor';
    }
  }

  if (
    supportsContourLevels(layer.layer_type) &&
    !levelsAreValid(layer.contour_levels)
  ) {
    errors.levels = 'invalid';
  }

  if (isVectorType(layer.layer_type)) {
    const downscaling = parseNumber(layer.downscaling);
    if (
      downscaling === null ||
      !Number.isInteger(downscaling) ||
      downscaling <= 0
    ) {
      errors.downscaling = 'invalid';
    }
  }

  // Drop undefined keys so `Object.keys(errors).length` is meaningful.
  (Object.keys(errors) as (keyof LayerValidation)[]).forEach((field) => {
    if (errors[field] === undefined) {
      delete errors[field];
    }
  });
  return errors;
};

// Backend rule: int(int(dpi * height) ** 2 * aspect) <= memory_budget_px2,
// where aspect = max(2.0, extent lon-span / lat-span). 2.0 is the FLOOR used
// for extent-less (full-grid) configs — a wide custom extent raises it, so
// mirroring without the aspect under-estimates and lets the composer submit
// parameters the backend will always 400.
export const memoryBudgetUsage = (
  height: number,
  dpi: number,
  aspect = 2,
): number => Math.trunc(Math.trunc(dpi * height) ** 2 * aspect);

export const validateDraft = (
  draft: BuilderDraft,
  capabilities: ForecastMapCapabilities,
): DraftValidation => {
  const model: ForecastMapCapabilityModel | null =
    capabilities.models.find((entry) => entry.id === draft.modelId) ?? null;
  const metrics = model ? model.metrics : null;

  const layerErrors: Record<string, LayerValidation> = {};
  draft.layers.forEach((layer) => {
    const errors = validateLayer(layer, metrics);
    if (Object.keys(errors).length > 0) {
      layerErrors[layer.key] = errors;
    }
  });

  const limits = capabilities.limits;
  const height = parseNumber(draft.height);
  const dpi = parseNumber(draft.dpi);
  const heightValid =
    height !== null &&
    height >= limits.height.min &&
    height <= limits.height.max;
  // dpi is a pydantic int on the backend — reject fractional values.
  const dpiValid =
    dpi !== null &&
    Number.isInteger(dpi) &&
    dpi >= limits.dpi.min &&
    dpi <= limits.dpi.max;

  let extentError: 'invalid' | undefined;
  let aspect = 2;
  if (draft.extentEnabled) {
    const minLon = parseNumber(draft.extent.min_lon);
    const maxLon = parseNumber(draft.extent.max_lon);
    const minLat = parseNumber(draft.extent.min_lat);
    const maxLat = parseNumber(draft.extent.max_lat);
    const bounded =
      minLon !== null &&
      maxLon !== null &&
      minLat !== null &&
      maxLat !== null &&
      minLon >= -180 &&
      maxLon <= 180 &&
      minLat >= -90 &&
      maxLat <= 90;
    if (!bounded || minLon! >= maxLon! || minLat! >= maxLat!) {
      extentError = 'invalid';
    } else {
      // A valid custom extent feeds the memory budget its true aspect —
      // the backend validator does the same, so the meter and the 400s agree.
      aspect = Math.max(aspect, (maxLon! - minLon!) / (maxLat! - minLat!));
    }
  }

  const used =
    height !== null && dpi !== null
      ? memoryBudgetUsage(height, dpi, aspect)
      : 0;
  const budgetExceeded = used > limits.memory_budget_px2;

  const layersEmpty = draft.layers.length === 0;
  const tooManyLayers = draft.layers.length > limits.max_layers;
  const nameMissing = draft.name.trim() === '';

  const parametersValid =
    draft.modelId !== null &&
    !layersEmpty &&
    !tooManyLayers &&
    Object.keys(layerErrors).length === 0 &&
    heightValid &&
    dpiValid &&
    !budgetExceeded &&
    extentError === undefined;

  return {
    layerErrors,
    model: draft.modelId === null ? 'required' : undefined,
    layersEmpty,
    tooManyLayers,
    extent: extentError,
    height: heightValid ? undefined : 'range',
    dpi: dpiValid ? undefined : 'range',
    budget: {
      used,
      limit: limits.memory_budget_px2,
      exceeded: budgetExceeded,
    },
    nameMissing,
    parametersValid,
    canSave: parametersValid && !nameMissing,
  };
};

// --- Draft → parameters -----------------------------------------------------

const levelsToApi = (
  levels: ContourLevelsDraft,
): ForecastMapContourLevels | undefined => {
  if (!levels.enabled) {
    return undefined;
  }
  if (levels.mode === 'linspace') {
    return {mode: 'linspace', count: parseNumber(levels.count)};
  }
  if (levels.mode === 'arange') {
    return {mode: 'arange', step: parseNumber(levels.step)};
  }
  return {mode: 'explicit', values: parseValuesList(levels.values) ?? []};
};

const colormapToApi = (
  colormap: ColormapDraft,
): ForecastMapLayer['colormap'] =>
  colormap.mode === 'custom'
    ? colormap.colors.map((color) => color.trim())
    : (colormap.preset as ForecastMapLayer['colormap']);

const layerToApi = (layer: LayerDraft, zOrder: number): ForecastMapLayer => {
  const min = parseNumber(layer.min_value);
  const max = parseNumber(layer.max_value);
  const base = {
    display_name: layer.display_name.trim(),
    colormap: colormapToApi(layer.colormap),
    ...(min !== null ? {min_value: min} : {}),
    ...(max !== null ? {max_value: max} : {}),
    opacity: layer.opacity,
    z_order: zOrder,
    nan_policy: layer.nan_policy,
  };
  if (layer.layer_type === 'barbs' || layer.layer_type === 'vector_field') {
    return {
      ...base,
      layer_type: layer.layer_type,
      u_metric: layer.u_metric,
      v_metric: layer.v_metric,
      downscaling: parseNumber(layer.downscaling) ?? 1,
      unit_conversion: layer.unit_conversion,
    };
  }
  if (layer.layer_type === 'magnitude') {
    const levels = levelsToApi(layer.contour_levels);
    return {
      ...base,
      layer_type: 'magnitude',
      u_metric: layer.u_metric,
      v_metric: layer.v_metric,
      ...(levels ? {contour_levels: levels} : {}),
      unit_conversion: layer.unit_conversion,
    };
  }
  const levels = supportsContourLevels(layer.layer_type)
    ? levelsToApi(layer.contour_levels)
    : undefined;
  return {
    ...base,
    layer_type: layer.layer_type,
    metric: layer.metric,
    ...(levels ? {contour_levels: levels} : {}),
    unit_conversion: layer.unit_conversion,
  };
};

/**
 * Serialize the draft into the backend parameters payload. Callers must
 * gate on validateDraft(...).parametersValid — this performs no checks.
 */
export const draftToParameters = (
  draft: BuilderDraft,
): ForecastMapParameters => ({
  // Draft is top-first; the API payload is bottom-first with ascending
  // z_order (bottom = 1), matching the preset convention.
  layers: [...draft.layers]
    .reverse()
    .map((layer, index) => layerToApi(layer, index + 1)),
  ...(draft.extentEnabled
    ? {
        extent: {
          min_lon: parseNumber(draft.extent.min_lon) ?? 0,
          max_lon: parseNumber(draft.extent.max_lon) ?? 0,
          min_lat: parseNumber(draft.extent.min_lat) ?? 0,
          max_lat: parseNumber(draft.extent.max_lat) ?? 0,
        },
      }
    : {}),
  projection: draft.projection,
  height: parseNumber(draft.height) ?? 16,
  dpi: parseNumber(draft.dpi) ?? 200,
  // Loaded overlays pass through untouched (no composer UI yet); absent
  // for new maps so the backend defaults apply.
  ...(draft.baseOverlays ? {base_overlays: draft.baseOverlays} : {}),
});

/** Stable serialization for dirty-state comparison. */
export const serializeDraft = (draft: BuilderDraft): string =>
  JSON.stringify(draft);
